import { Modal } from '@bp/ui-components';
import classNames from 'classnames';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './LegendModal.module.scss';

type LegendModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type LegendItem = { swatchClasses: string[]; labelKey: string };

// The swatch classes mirror the real grid styles (TimetableGrid.module.scss / GridCard.module.scss)
// via the same CSS custom properties, so the legend can never drift from what the grid actually shows.
const CELL_ITEMS: LegendItem[] = [
  { swatchClasses: ['cell-available'], labelKey: 'legend.cells.available' },
  { swatchClasses: ['cell-warning'], labelKey: 'legend.cells.limited' },
  { swatchClasses: ['cell-blocked'], labelKey: 'legend.cells.blocked' },
  { swatchClasses: ['card', 'cell-swap-conflict'], labelKey: 'legend.cells.swapConflict' },
  { swatchClasses: ['card', 'cell-swap-limited'], labelKey: 'legend.cells.swapLimited' },
  { swatchClasses: ['cell-finding'], labelKey: 'legend.cells.finding' },
  { swatchClasses: ['cell-pause'], labelKey: 'legend.cells.pause' },
];

const CARD_ITEMS: LegendItem[] = [
  { swatchClasses: ['card'], labelKey: 'legend.cards.placed' },
  { swatchClasses: ['card', 'card-locked'], labelKey: 'legend.cards.locked' },
  { swatchClasses: ['card', 'card-highlight'], labelKey: 'legend.cards.highlighted' },
  { swatchClasses: ['card', 'card-lowlight'], labelKey: 'legend.cards.dimmed' },
  { swatchClasses: ['card', 'card-conflict'], labelKey: 'legend.cards.conflict' },
  { swatchClasses: ['card', 'card-preview'], labelKey: 'legend.cards.preview' },
];

const LegendList: FC<{ items: LegendItem[] }> = ({ items }) => {
  const { t } = useTranslation();
  return (
    <ul className={styles.list}>
      {items.map((item) => (
        <li className={styles.item} key={item.labelKey}>
          <span className={classNames(styles.swatch, ...item.swatchClasses.map((c) => styles[c]))} />
          {t(item.labelKey)}
        </li>
      ))}
    </ul>
  );
};

export const LegendModal: FC<LegendModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      title={t('legend.title')}
      parent={document.fullscreenElement ? document.getElementById('timetable-grid') : null}
    >
      <div className={styles.section}>
        <div className={styles['section-title']}>{t('legend.cells.title')}</div>
        <div className={styles['section-hint']}>{t('legend.cells.hint')}</div>
        <LegendList items={CELL_ITEMS} />
      </div>
      <div className={styles.section}>
        <div className={styles['section-title']}>{t('legend.cards.title')}</div>
        <LegendList items={CARD_ITEMS} />
      </div>
    </Modal>
  );
};
